import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGasI18n, useGasNotify, useGasConfirm, GasAjaxError } from '../../shared/kit';
import { formatPrice } from '../shop-shared/format';
import {
    applyCoupon as apiApplyCoupon,
    emptyCart as apiEmptyCart,
    getCartFullDetails,
    removeCoupon as apiRemoveCoupon,
    removeFromCart,
    updateCartQuantity,
} from './api';
import type { AppliedCoupon, CartItem, ShopCartInitData } from './types';

interface AppProps {
    initData: ShopCartInitData;
}

export default function App({ initData }: AppProps) {
    const { t, tn } = useGasI18n();
    const notify = useGasNotify();
    const { confirm } = useGasConfirm();

    const cfg = {
        shop_url: initData.shop_url || '/agenda/',
        cart_url: initData.cart_url || '/shop/panier/',
        checkout_url: initData.checkout_url || '/shop/checkout/',
    };

    const [items, setItems] = useState<CartItem[]>(Array.isArray(initData.items) ? initData.items : []);
    const [count, setCount] = useState<number>(Number(initData.count || 0));
    const [subtotal, setSubtotal] = useState<number>(Number(initData.subtotal || 0));
    const [discountTotal, setDiscountTotal] = useState<number>(Number(initData.discount_total || 0));
    const [total, setTotal] = useState<number>(Number(initData.total || 0));
    const [isEmpty, setIsEmpty] = useState<boolean>(Boolean(initData.is_empty));
    const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(initData.applied_coupon || null);
    const [mergeMessages] = useState(Array.isArray(initData.merge_messages) ? initData.merge_messages : []);

    const [couponCode, setCouponCode] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponFeedback, setCouponFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [removingItems, setRemovingItems] = useState<Record<number, boolean>>({});
    const [updatingItems, setUpdatingItems] = useState<Record<number, boolean>>({});
    const [emptyingCart, setEmptyingCart] = useState(false);

    const hasAlreadyPurchasedItems = useMemo(
        () => items.some((it) => it?.display_info?.already_purchased),
        [items],
    );

    const updateGlobalCartInfo = useCallback((cartCount: number, cartTotal: number) => {
        document.querySelectorAll('.gas-shop-cart-count').forEach((n) => { n.textContent = String(cartCount); });
        document.querySelectorAll('.gas-shop-cart-total').forEach((n) => { n.textContent = formatPrice(cartTotal); });
        document.dispatchEvent(new CustomEvent('gas_cart_updated', { detail: { cart_count: cartCount, cart_total: cartTotal } }));
    }, []);

    const applyServerData = useCallback((data: {
        items?: CartItem[]; count?: number; subtotal?: number; discount_total?: number;
        total?: number; is_empty?: boolean; applied_coupon?: AppliedCoupon | null;
    }) => {
        setItems(data.items || []);
        setCount(Number(data.count || 0));
        setSubtotal(Number(data.subtotal || 0));
        setDiscountTotal(Number(data.discount_total || 0));
        setTotal(Number(data.total || 0));
        setIsEmpty(Boolean(data.is_empty));
        setAppliedCoupon(data.applied_coupon || null);
        updateGlobalCartInfo(Number(data.count || 0), Number(data.total || 0));
    }, [updateGlobalCartInfo]);

    const refreshCartFull = useCallback(async () => {
        const data = await getCartFullDetails(initData);
        applyServerData(data);
    }, [initData, applyServerData]);

    useEffect(() => {
        refreshCartFull().catch(() => { /* tolère un échec réseau au montage */ });
    }, [refreshCartFull]);

    const freeTicketsRemainingAfter = useCallback((item: CartItem) => {
        const info = item.free_tickets_info;
        if (!info) return 0;
        const usedAfter = (info.free_used_before || 0) + (info.free_in_cart || 0);
        return Math.max(0, (info.total_free_allowed || 0) - usedAfter);
    }, []);

    const updateQuantity = useCallback(async (item: CartItem, newQty: number) => {
        const oldQty = item.quantity;
        setUpdatingItems((p) => ({ ...p, [item.id]: true }));
        setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, quantity: newQty } : it)));
        try {
            await updateCartQuantity(initData, item.id, newQty);
            await refreshCartFull();
        } catch (error) {
            setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, quantity: oldQty } : it)));
            notify.error((error as Error).message || t('errors.generic'));
        } finally {
            setUpdatingItems((p) => ({ ...p, [item.id]: false }));
        }
    }, [initData, refreshCartFull, notify, t]);

    const removeItem = useCallback(async (item: CartItem) => {
        const ok = await confirm({
            message: t('shop.cart.confirm_remove'),
            confirmLabel: t('shop.cart.remove'),
            tone: 'danger',
        });
        if (!ok) return;
        setRemovingItems((p) => ({ ...p, [item.id]: true }));
        try {
            await removeFromCart(initData, item.id);
            setItems((prev) => prev.filter((it) => it.id !== item.id));
            await refreshCartFull();
        } catch (error) {
            notify.error((error as Error).message || t('errors.generic'));
        } finally {
            setRemovingItems((p) => ({ ...p, [item.id]: false }));
        }
    }, [initData, refreshCartFull, confirm, notify, t]);

    const doApplyCoupon = useCallback(async () => {
        if (!couponCode) return;
        setCouponLoading(true);
        setCouponFeedback(null);
        try {
            const data = await apiApplyCoupon(initData, couponCode);
            setCouponCode('');
            setCouponFeedback({ type: 'success', message: data.message || t('shop.coupon.applied') });
            await refreshCartFull();
        } catch (error) {
            const message = error instanceof GasAjaxError ? error.message : t('errors.generic');
            setCouponFeedback({ type: 'error', message });
        } finally {
            setCouponLoading(false);
        }
    }, [couponCode, initData, refreshCartFull, t]);

    const doRemoveCoupon = useCallback(async () => {
        setCouponLoading(true);
        setCouponFeedback(null);
        try {
            await apiRemoveCoupon(initData);
            await refreshCartFull();
        } catch (error) {
            setCouponFeedback({ type: 'error', message: (error as Error).message || t('errors.generic') });
        } finally {
            setCouponLoading(false);
        }
    }, [initData, refreshCartFull, t]);

    const emptyCart = useCallback(async () => {
        const ok = await confirm({
            message: t('shop.cart.confirm_empty'),
            confirmLabel: t('shop.cart.empty_action'),
            tone: 'danger',
        });
        if (!ok) return;
        setEmptyingCart(true);
        try {
            await apiEmptyCart(initData);
            applyServerData({ items: [], count: 0, subtotal: 0, discount_total: 0, total: 0, is_empty: true, applied_coupon: null });
            notify.success(t('shop.cart.emptied'));
        } catch (error) {
            notify.error((error as Error).message || t('errors.generic'));
        } finally {
            setEmptyingCart(false);
        }
    }, [initData, applyServerData, confirm, notify, t]);

    // ---- Rendu ----

    return (
        <div>
            {mergeMessages.length > 0 && (
                <div className="gas-shop-merge-messages">
                    {mergeMessages.map((msg, mi) => (
                        <div key={mi} className={`gas-shop-message gas-shop-message-${msg.type}`}>{msg.text}</div>
                    ))}
                </div>
            )}

            {hasAlreadyPurchasedItems && (
                <div className="gas-shop-global-info-message">
                    <span className="gas-info-icon">ℹ️</span>
                    <span className="gas-info-text">{t('shop.cart.already_purchased_notice')}</span>
                </div>
            )}

            {isEmpty ? (
                <div className="gas-shop-panier-vide">
                    <p>{t('shop.cart.empty')}</p>
                    <a href={cfg.shop_url} className="gas-shop-action-button">{t('shop.cart.discover_activities')}</a>
                </div>
            ) : (
                <div className="gas-shop-panier-content">
                    <div className="gas-shop-panier-items">
                        {items.map((item) => (
                            <CartItemRow
                                key={item.id}
                                item={item}
                                removing={Boolean(removingItems[item.id])}
                                updating={Boolean(updatingItems[item.id])}
                                onQuantity={(qty) => updateQuantity(item, qty)}
                                onRemove={() => removeItem(item)}
                                freeTicketsRemainingAfter={freeTicketsRemainingAfter}
                                t={t}
                                tn={tn}
                            />
                        ))}
                    </div>

                    <div className="gas-shop-panier-summary">
                        <div className="gas-shop-coupon-section">
                            <h3 className="gas-shop-summary-title">{t('shop.coupon.title')}</h3>

                            {appliedCoupon ? (
                                <div className="gas-shop-applied-coupon">
                                    <div className="gas-shop-applied-coupon-header">
                                        <div>
                                            <strong>{appliedCoupon.code}</strong>
                                            <p className="gas-shop-applied-coupon-hint">
                                                {(appliedCoupon.scope_type || 'global') === 'product'
                                                    ? t('shop.coupon.scope_product')
                                                    : (appliedCoupon.scope_type || 'global') === 'product_type'
                                                        ? t('shop.coupon.scope_product_type')
                                                        : t('shop.coupon.scope_global')}
                                            </p>
                                        </div>
                                        <button type="button" className="gas-shop-remove-coupon-button" disabled={couponLoading} onClick={doRemoveCoupon}>
                                            {couponLoading ? t('shop.coupon.removing') : t('shop.coupon.remove')}
                                        </button>
                                    </div>
                                    <div className="gas-shop-applied-coupon-meta">
                                        <span>{t('shop.coupon.eligible_subtotal')} {formatPrice(appliedCoupon.eligible_subtotal || 0)}</span>
                                        <strong>-{formatPrice(appliedCoupon.discount_amount || 0)}</strong>
                                    </div>
                                </div>
                            ) : (
                                <form className="gas-shop-coupon-form" onSubmit={(e) => { e.preventDefault(); doApplyCoupon(); }}>
                                    <label htmlFor="gas-coupon-code">{t('shop.coupon.label')}</label>
                                    <div className="gas-shop-coupon-input-row">
                                        <input
                                            type="text"
                                            id="gas-coupon-code"
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value.trim())}
                                            placeholder={t('shop.coupon.placeholder')}
                                            autoComplete="off"
                                        />
                                        <button type="submit" className="gas-shop-apply-coupon-button" disabled={couponLoading || !couponCode}>
                                            {couponLoading ? t('shop.coupon.applying') : t('shop.coupon.apply')}
                                        </button>
                                    </div>
                                    <p className="gas-shop-coupon-help">{t('shop.coupon.help')}</p>
                                </form>
                            )}

                            {couponFeedback && (
                                <div className={`gas-shop-coupon-feedback gas-shop-coupon-feedback-${couponFeedback.type}`}>
                                    {couponFeedback.message}
                                </div>
                            )}
                        </div>

                        <div className="gas-shop-total-section">
                            <div className="gas-shop-subtotal">
                                <span dangerouslySetInnerHTML={{ __html: t('shop.cart.subtotal_with_items', { items: tn('shop.cart.item_count', count) }) }} />
                                <strong>{formatPrice(subtotal)}</strong>
                            </div>
                            {discountTotal > 0 && (
                                <div className="gas-shop-discount">
                                    <span>{t('shop.cart.coupon_discount')}</span>
                                    <strong>-{formatPrice(discountTotal)}</strong>
                                </div>
                            )}
                            <div className="gas-shop-total">
                                <span>{t('shop.cart.total_label')}</span>
                                <strong className="gas-shop-total-amount">{formatPrice(total)}</strong>
                            </div>
                        </div>

                        <div className="gas-shop-checkout-actions">
                            <a href={cfg.shop_url} className="gas-shop-continue-shopping-link">{t('shop.cart.continue_shopping')}</a>
                            <a href={cfg.checkout_url} className="gas-shop-checkout-button">{t('shop.cart.proceed_to_payment')}</a>
                            <button type="button" className="gas-shop-empty-cart-button" disabled={emptyingCart} onClick={emptyCart}>
                                {emptyingCart ? t('shop.cart.emptying') : t('shop.cart.empty_action')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

interface CartItemRowProps {
    item: CartItem;
    removing: boolean;
    updating: boolean;
    onQuantity: (qty: number) => void;
    onRemove: () => void;
    freeTicketsRemainingAfter: (item: CartItem) => number;
    t: (key: string, params?: Record<string, string | number>) => string;
    tn: (key: string, count: number, params?: Record<string, string | number>) => string;
}

function CartItemRow({ item, removing, updating, onQuantity, onRemove, freeTicketsRemainingAfter, t, tn }: CartItemRowProps) {
    const cls = ['gas-shop-panier-item'];
    if (removing) cls.push('gas-shop-panier-item--removing');
    if (updating) cls.push('gas-shop-panier-item--updating');

    const di = item.display_info;
    const fti = item.free_tickets_info;

    return (
        <div className={cls.join(' ')} data-item-id={item.id}>
            <div className="gas-shop-item-header">
                <div className="gas-shop-item-image">
                    <a href={item.product_url} className="gas-shop-product-link">
                        <img src={item.image_url} alt={item.product_title} loading="lazy" />
                    </a>
                </div>
                <div className="gas-shop-item-main-info">
                    <h3 className="gas-shop-item-title">
                        <a href={item.product_url} className="gas-shop-product-link">{item.product_title}</a>
                    </h3>
                    <div className="gas-shop-item-price-info">
                        {item.price_breakdown && item.price_breakdown.has_mixed_pricing ? (
                            <div className="gas-shop-price-free-tickets-mixed">
                                <div className="gas-shop-price-label">{t('shop.cart.price_breakdown')}</div>
                                <div className="gas-shop-price-breakdown-detail">
                                    <span className="gas-shop-free-part">
                                        <strong>{tn('shop.cart.free_ticket_count', item.price_breakdown.free_count)}</strong>
                                        <span className="gas-shop-member-badge-mini">{t('access.member')}</span>
                                    </span>
                                    {' + '}
                                    <span className="gas-shop-paid-part">
                                        <strong>{item.price_breakdown.paid_count}</strong> ×{' '}
                                        {formatPrice(item.price_breakdown.paid_unit_price)} ={' '}
                                        {formatPrice(item.price_breakdown.paid_total)}
                                    </span>
                                </div>
                                <div className="gas-shop-price-total-line">
                                    <span className="gas-shop-price-label">{t('shop.cart.total_label')}</span>
                                    <span className="gas-shop-price-current">{formatPrice(item.prix_total)}</span>
                                </div>
                            </div>
                        ) : item.price_breakdown && item.price_breakdown.all_free ? (
                            <div className="gas-shop-price-all-free">
                                <span className="gas-shop-price-free">{t('shop.cart.free_with_ticket_count', { count: tn('shop.cart.ticket_count', item.price_breakdown.free_count) })}</span>
                                <small className="gas-shop-member-badge">{t('shop.cart.member_free_tickets')}</small>
                            </div>
                        ) : item.prix_details && item.prix_details.has_discount ? (
                            <div className="gas-shop-price-with-discount">
                                <span className="gas-shop-price-current">{formatPrice(item.prix_unitaire)}</span>
                                <span className="gas-shop-price-original"><del>{formatPrice(item.prix_details.prix_base)}</del></span>
                                {item.prix_details.rabais_appliques && item.prix_details.rabais_appliques.length > 0 && (
                                    <div className="gas-shop-discount-details">
                                        {item.prix_details.rabais_appliques.map((r, ri) => (
                                            <small key={ri} className="gas-shop-discount-badge">{r}</small>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <span className="gas-shop-price-current">{formatPrice(item.prix_unitaire)}</span>
                        )}
                    </div>
                </div>
            </div>

            {item.variante && (
                <div className="gas-shop-item-variant-full">
                    <span className="gas-shop-variant-icon">{item.variante.icon || ''}</span>
                    <span className="gas-shop-variant-name">{item.variante.nom || ''}</span>
                    {item.variante.description && (
                        <span className="gas-shop-variant-description">- {item.variante.description}</span>
                    )}
                </div>
            )}

            {item.is_invitation && (
                <div className="gas-shop-item-invitation-badge">
                    <span className="gas-invitation-icon">🎁</span>
                    <span className="gas-invitation-text">{t('shop.cart.free_invitation')}</span>
                </div>
            )}

            {di && di.message && (
                <div className="gas-shop-item-info-badges">
                    {di.show_login_prompt ? (
                        <div className="gas-shop-info-badge gas-shop-badge-login-prompt">
                            <span className="gas-badge-icon">🔐</span>
                            <span className="gas-badge-text">{di.message}</span>
                        </div>
                    ) : (
                        <div className={`gas-shop-info-badge gas-shop-badge-${di.badge_type || 'info'}`}>
                            <span className="gas-badge-icon">{di.limit_reached ? '⚠️' : di.already_purchased ? '✓' : 'ℹ️'}</span>
                            <span className="gas-badge-text">{di.message}</span>
                        </div>
                    )}
                </div>
            )}

            {item.pricing_notice && item.pricing_notice.message && (
                <div className="gas-shop-pricing-notice-cart">
                    <span className="gas-pricing-icon">💰</span>
                    <span className="gas-pricing-text" dangerouslySetInnerHTML={{ __html: item.pricing_notice.message }} />
                </div>
            )}

            {fti && fti.has_free_in_cart && (
                <div className="gas-shop-free-tickets-cart-info">
                    <div className="gas-shop-free-tickets-summary">
                        <span className="gas-free-tickets-icon">🎁</span>
                        <span className="gas-free-tickets-details">
                            <strong>{t('shop.cart.member_free_tickets_label')}</strong>{' '}
                            {fti.free_in_cart > 0 && fti.paid_in_cart > 0 ? (
                                <>
                                    {tn('shop.cart.free_ticket_count', fti.free_in_cart)}
                                    {' + '}
                                    {tn('shop.cart.paid_ticket_count', fti.paid_in_cart)}
                                </>
                            ) : (
                                t('shop.cart.free_in_item', { count: tn('shop.cart.ticket_count', fti.free_in_cart) })
                            )}
                        </span>
                    </div>
                    <div className="gas-shop-free-tickets-progress">
                        <small>
                            {t('shop.cart.after_order_usage', {
                                used: (fti.free_used_before || 0) + fti.free_in_cart,
                                total: fti.total_free_allowed,
                            })}
                            {freeTicketsRemainingAfter(item) > 0 && (
                                <> ({tn('shop.cart.remaining_count', freeTicketsRemainingAfter(item))})</>
                            )}
                        </small>
                    </div>
                </div>
            )}

            {item.cart_message && (
                <div className="gas-shop-item-cart-message">
                    <span className="gas-shop-cart-message-icon">ℹ️</span>
                    <span className="gas-shop-cart-message-text">{item.cart_message}</span>
                </div>
            )}

            <div className="gas-shop-item-controls">
                <div className="gas-shop-item-quantity">
                    {item.is_invitation ? (
                        <>
                            <span className="gas-shop-quantity-fixed gas-shop-invitation-quantity">{t('shop.cart.quantity_invitation')}</span>
                            <small className="gas-shop-quantity-info gas-shop-invitation-info">{t('shop.cart.quantity_invitation_info')}</small>
                        </>
                    ) : item.allows_multiple && item.max_effective > 1 ? (
                        <>
                            <label htmlFor={`quantity_${item.id}`}>{t('shop.cart.quantity_label')}</label>
                            <select
                                className="gas-shop-quantity-select"
                                id={`quantity_${item.id}`}
                                value={item.quantity}
                                onChange={(e) => onQuantity(parseInt(e.target.value, 10))}
                            >
                                {Array.from({ length: item.max_effective }, (_, i) => i + 1).map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                            <small className="gas-shop-quantity-info">
                                {item.current_user_total > 0
                                    ? t('shop.cart.quantity_summary', {
                                        ordered: item.current_user_total,
                                        in_cart: item.quantity,
                                        total: item.current_user_total + item.quantity,
                                        max: item.max_per_person,
                                    })
                                    : item.max_per_commande < item.max_per_person
                                        ? t('shop.cart.max_per_order', { max: item.max_per_commande })
                                        : t('shop.cart.max_total_tickets', { max: item.max_per_person })}
                            </small>
                        </>
                    ) : (
                        <span className="gas-shop-quantity-fixed">{t('shop.cart.quantity_fixed', { quantity: item.quantity })}</span>
                    )}
                </div>

                <div className="gas-shop-item-total">
                    <strong>{formatPrice(item.prix_total)}</strong>
                </div>

                <div className="gas-shop-item-actions">
                    <button type="button" className="gas-shop-remove-item" disabled={removing} onClick={onRemove} title={t('shop.cart.remove_item_title')}>×</button>
                </div>
            </div>
        </div>
    );
}
